import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Loader from "react-loader-spinner";
import LiveRegion from "./liveRegion";
import Style from "./loadingIndicator.module.css";

/**
 * Page level busy indicator: a dimmed backdrop keeps the spinner readable over
 * any page. It covers the page content but deliberately sits below the sidebar
 * (and below any open modal), so it indicates work rather than blocking it.
 *
 * Two ways to use it:
 *
 *   {isLoading && <LoadingIndicator />}   mounts on demand, fades in, and
 *                                         disappears at once when unmounted
 *   <LoadingIndicator visible={isLoading} />  additionally fades out
 *
 * The second form still renders nothing while idle - it mounts itself when it
 * becomes visible and unmounts once the fade out has finished - so it cannot
 * leave a stray overlay covering the page.
 */
const LoadingIndicator = ({ visible = true }) => {
  const { t } = useTranslation();
  const [rendered, setRendered] = useState(visible);

  useEffect(() => {
    if (visible) {
      setRendered(true);
    }
  }, [visible]);

  if (!rendered) {
    return null;
  }

  const handleAnimationEnd = (event) => {
    // the spinner animates too, ignore anything bubbling up from it
    if (event.target === event.currentTarget && !visible) {
      setRendered(false);
    }
  };

  const className = visible
    ? Style.loadingIndicatorOverlay
    : `${Style.loadingIndicatorOverlay} ${Style.loadingIndicatorOverlayLeaving}`;

  return (
    <div className={className} onAnimationEnd={handleAnimationEnd}>
      <LiveRegion message={visible ? t("notifications.loading") : ""} />
      <div className={Style.loadingIndicatorWrapper} aria-hidden="true">
        <Loader type="Oval" color="#005a92" height={80} width={80} />
      </div>
    </div>
  );
};

export default LoadingIndicator;
